/* eslint-disable formatjs/no-literal-string-in-jsx */
import React, { memo } from 'react';
import { useIntl } from 'react-intl';
import {
  ApplicationIcon,
  Box,
  Link as DSLink,
  Typography,
} from '@saviynt/design-system';

import { CF_NEU_STL, CF_SEC_BLD, l } from '../../../../../shared/constants';
import { getStatusKind } from '../../../utils';

import './SidePanelAccountItem.css';

interface SidePanelAccountItemProps {
  serviceAccountName: string;
  serviceAccountEndpoint: string;
  serviceAccountStatus: string;
  handleUnlinkBtn: () => void;
  isDisabled?: boolean;
}

/**
 * Displays a single service account row inside the side panel,
 * showing the account name, endpoint, inactive styling, and an Unlink action.
 * @param props - {@link SidePanelAccountItemProps}
 * @param props.serviceAccountName - Display name of the service account.
 * @param props.serviceAccountEndpoint - Endpoint label for the service account.
 * @param props.serviceAccountStatus - Status string (e.g. "active" or "inactive").
 * @param props.handleUnlinkBtn - Callback invoked when the Unlink action is clicked.
 * @param props.isDisabled - Disables the Unlink action when true.
 * @returns The rendered service account row.
 */
function SidePanelAccountItem({
  serviceAccountName,
  serviceAccountEndpoint,
  serviceAccountStatus,
  handleUnlinkBtn,
  isDisabled,
}: SidePanelAccountItemProps) {
  const intl = useIntl();

  const isInactive = serviceAccountStatus.toLowerCase() === 'inactive';

  const { statusColor, statusTextKey, statusText } =
    getStatusKind(serviceAccountStatus);

  return (
    <Box tag='div' className='SidePanelAccountItem'>
      <ApplicationIcon icon='ServiceAccount' />
      <Box tag='div' className='SidePanelAccountItem-detailsWrapper'>
        <Typography
          kind='body2'
          color={isInactive ? CF_NEU_STL : CF_SEC_BLD}
          lineLimit={1}>
          {serviceAccountName}
        </Typography>
        <Typography kind='body2' color={CF_NEU_STL} lineLimit={1}>
          {serviceAccountEndpoint}
        </Typography>
      </Box>
      {statusText.trim().length > 0 && (
        <Typography
          kind='body3'
          color={statusColor}
          className='SidePanelAccountItem-status'>
          {`● ${intl.formatMessage(l[statusTextKey])}`}
        </Typography>
      )}
      <DSLink
        text={intl.formatMessage({
          id: 'uI878f',
          defaultMessage: 'Unlink',
          description: 'Unlink',
        })}
        kind='button'
        isDisabled={isDisabled}
        onClick={handleUnlinkBtn}
      />
    </Box>
  );
}

SidePanelAccountItem.defaultProps = {
  isDisabled: false,
};

export default memo(SidePanelAccountItem);
